import { InjectionToken, inject } from "@angular/core";
import { HttpContextToken, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from "@angular/common/http";
import { EMPTY, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { ErrorHandlerService } from "../_services/error-handler.service";
import { SIGN_IN_REDIRECT, SignInRedirect } from "../_guards/sign-in-redirect";

export const BASE_API_URL = new InjectionToken<string>("BASE_API_URL");

// A request that handles its own 401: a refused sign-in, where /sign-in would start the same sign-in again, or a check
// whose page decides where a refused token goes.
export const SKIP_SIGN_IN_REDIRECT_ON_401 = new HttpContextToken<boolean>(() => false);

export const baseHttpRequestInterceptor: () => HttpInterceptorFn = () => {
    const interceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
        if (request.url.includes("/assets/i18n/")) {
            return next(request);
        }

        const baseUrl: string = inject(BASE_API_URL);
        const router: Router = inject(Router);
        const redirectToSignIn: SignInRedirect = inject(SIGN_IN_REDIRECT);
        const errorHandlerService: ErrorHandlerService | null = inject(ErrorHandlerService, { optional: true });

        const token: string = localStorage.getItem("token") ?? "";

        const apiReq = request.clone({
            url: `${baseUrl}${request.url}`,
            headers: request.headers
                .set("Authorization", `Bearer ${token}`)
                .set("uid", token)
                .set("Cache-Control", "no-cache")
                .set("Pragma", "no-cache")
                .set("Expires", "Sat, 01 Jan 2000 00:00:00 GMT"),
        });

        return next(apiReq).pipe(
            map((event) => {
                if (event instanceof HttpResponse) {
                    if (event.headers.has("access_token")) {
                        const hToken = event.headers.get("access_token");
                        const userId = event.headers.get("user_id");

                        if (hToken) {
                            localStorage.setItem("token", hToken);
                            if (userId) {
                                localStorage.setItem("userId", userId);
                            }
                        }
                    }
                }

                return event;
            }),
            catchError((err) => {
                let handled = false;
                if (err.status === 401 && !request.context.get(SKIP_SIGN_IN_REDIRECT_ON_401)) {
                    // The stored token was refused, most often expired: the page asked for is kept for the sign-in.
                    localStorage.removeItem("token");
                    localStorage.removeItem("userId");
                    redirectToSignIn(router.url);
                    handled = true;
                } else if (err.status === 403 && errorHandlerService) {
                    errorHandlerService.handleForbiddenError(err.error);
                    handled = true;
                }

                if (handled) {
                    return EMPTY;
                }

                return throwError(() => err);
            }),
        );
    };

    return interceptor;
};
